import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconDownload,
  IconMail,
  IconLock,
  IconTool,
  IconClock,
  IconUser,
  IconCurrencyPound,
} from '@tabler/icons-react';

// Record types are our own enum, so the heading is a translated phrase rather
// than the raw value with a word appended — word order differs by language.
const RECORD_TITLE_KEYS = {
  service:    'service.record_title_service',
  repair:     'service.record_title_repair',
  inspection: 'service.record_title_inspection',
};

export default function ServiceRecordDetail() {
  const { t, language } = useTranslation();
  const { machineId, recordId } = useParams();
  const navigate = useNavigate();
  const { orgId } = useAuth();

  const [record, setRecord] = useState(null);
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !machineId || !recordId) return;
      try {
        const [recordSnap, machineSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', orgId, 'machines', machineId, 'serviceRecords', recordId)),
          getDoc(doc(db, 'organisations', orgId, 'machines', machineId)),
        ]);
        if (recordSnap.exists()) setRecord({ id: recordSnap.id, ...recordSnap.data() });
        if (machineSnap.exists()) setMachine({ id: machineSnap.id, ...machineSnap.data() });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, machineId, recordId]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const totalPartsCost = record?.parts?.reduce((sum, p) =>
    sum + (Number(p.cost) * Number(p.quantity) || 0), 0
  ) || 0;

  const totalCost = (Number(record?.cost) || 0) + totalPartsCost;

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        {t('common.loading')}
      </div>
    </Layout>
  );

  if (!record) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div
          onClick={() => navigate(`/machines/${machineId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('common.back')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('service.record_not_found')}</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        {/* Back */}
        <div
          onClick={() => navigate(`/machines/${machineId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} />
          {machine?.makeLabel} {machine?.modelLabel}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 22, fontWeight: 400, textTransform: 'capitalize' }}>
                {RECORD_TITLE_KEYS[record.type] ? t(RECORD_TITLE_KEYS[record.type]) : `${record.type} ${t('service.record_suffix')}`}
              </h1>
              {record.sealed && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, background: '#E6F1FB', color: '#185FA5', borderRadius: 'var(--radius-full)', padding: '3px 10px' }}>
                  <IconLock size={11} stroke={1.5} /> {t('service.sealed')}
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {machine?.makeLabel} {machine?.modelLabel}
              {machine?.registration && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 6px', borderRadius: 3, marginLeft: 8 }}>
                  {machine.registration}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              icon={<IconDownload size={13} stroke={1.5} />}
              onClick={() => toast.info(t('common.pdf_coming_soon'))}
            >
              {t('common.export_pdf')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<IconMail size={13} stroke={1.5} />}
              onClick={() => toast.info(t('service.email_coming_soon'))}
            >
              {t('service.email_btn')}
            </Button>
          </div>
        </div>

        {/* Sealed banner */}
        {record.sealed && (
          <div style={{
            background: '#E6F1FB',
            border: '0.5px solid #185FA5',
            borderRadius: 'var(--radius-lg)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20,
          }}>
            <IconLock size={16} color="#185FA5" stroke={1.5} />
            <div>
              <div style={{ fontSize: 13, color: '#185FA5' }}>
                {record.source === 'workshop'
                  ? t('service.sealed_by_workshop').replace('{name}', record.workshopName || t('service.the_workshop'))
                  : t('service.sealed_permanent')}
              </div>
              {/* A customer may not recognise the technician's name, so an
                  external record names the workshop and its job reference. */}
              <div style={{ fontSize: 12, color: '#185FA5', opacity: 0.7, marginTop: 2 }}>
                {t('service.sealed_by')} {record.sealedBy}
                {record.source === 'workshop' && record.workshopName ? ` ${t('service.at_suffix').replace('{name}', record.workshopName)}` : ''}
                {record.workshopJobRef ? ` · ${t('service.job_ref').replace('{ref}', record.workshopJobRef)}` : ''}
                {' · '}{formatDateTime(record.sealedAt)}
              </div>
            </div>
          </div>
        )}

        {/* Photos and video from the job. Copied into this organisation's own
            storage when the record arrived, so they stay available whatever
            happens to the workshop relationship. */}
        {Array.isArray(record.media) && record.media.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>{t('service.photos_video')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {record.media.map((m, i) => {
                const src = m.url || m.sourceUrl;
                if (!src) return null;
                if (m.type === 'video') {
                  return (
                    <video key={i} src={src} controls
                      style={{ width: 240, borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)' }} />
                  );
                }
                return (
                  <img key={i} src={src} alt={m.filename || t('service.job_photo')}
                    onClick={() => window.open(src, '_blank')}
                    style={{ width: 150, height: 112, objectFit: 'cover', cursor: 'pointer',
                             borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)' }} />
                );
              })}
            </div>
            {record.media.some(m => m.status === 'pending') && (
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 8 }}>
                {t('service.files_copying')}
              </div>
            )}
          </Card>
        )}

        {/* Key stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            {
              icon: IconClock,
              label: t('service.date_of_service'),
              value: record.dateOfService ? new Date(record.dateOfService).toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
              colour: 'var(--color-text-primary)',
            },
            {
              icon: IconTool,
              label: t('service.hours_at_service'),
              value: record.hoursAtService ? `${record.hoursAtService.toLocaleString()} ${t('common.unit_hrs')}` : '—',
              colour: 'var(--color-text-primary)',
            },
            {
              icon: IconCurrencyPound,
              label: t('service.total_cost'),
              value: totalCost ? `£${totalCost.toLocaleString()}` : '—',
              colour: 'var(--color-text-primary)',
            },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{s.label}</div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 400, color: s.colour }}>{s.value}</div>
              </div>
            );
          })}
        </div>

        {/* Details */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.record_details')}</div>
          {[
            { label: t('service.type'), value: record.type, capitalise: true },
            { label: t('defects.col_description'), value: record.description },
            { label: t('service.performed_by'), value: record.performedBy },
            { label: t('service.performed_by_type'), value: record.performedByType, capitalise: true },
            { label: t('service.date_of_service'), value: record.dateOfService ? new Date(record.dateOfService).toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
            { label: t('service.hours_at_service'), value: record.hoursAtService ? `${record.hoursAtService.toLocaleString()} ${t('common.unit_hrs')}` : '—' },
            { label: t('service.next_service_hours'), value: record.nextServiceHours ? `${record.nextServiceHours.toLocaleString()} ${t('common.unit_hrs')}` : '—' },
            { label: t('service.next_service_date'), value: record.nextServiceDate ? new Date(record.nextServiceDate).toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-primary)', textTransform: row.capitalise ? 'capitalize' : 'none', maxWidth: '60%', textAlign: 'right' }}>
                {row.value || '—'}
              </span>
            </div>
          ))}
          {record.notes && (
            <div style={{ padding: '10px 0' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>{t('service.notes')}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{record.notes}</div>
            </div>
          )}
        </Card>

        {/* Parts */}
        {record.parts?.filter(p => p.name).length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.parts_used')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px', gap: 0, padding: '6px 0', borderBottom: '0.5px solid var(--color-border-light)', marginBottom: 4 }}>
              {['service.col_part', 'service.col_qty', 'service.col_cost'].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{t(h)}</div>
              ))}
            </div>
            {record.parts.filter(p => p.name).map((part, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px', gap: 0, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{part.name}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{part.quantity}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {part.cost ? `£${(Number(part.cost) * Number(part.quantity)).toLocaleString()}` : '—'}
                </div>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px', gap: 0, padding: '10px 0' }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{t('service.parts_total')}</div>
              <div />
              <div style={{ fontSize: 13, fontWeight: 500 }}>£{totalPartsCost.toLocaleString()}</div>
            </div>
          </Card>
        )}

        {/* Cost breakdown */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.cost_breakdown')}</div>
          {[
            { label: t('service.labour_cost'), value: record.cost ? `£${Number(record.cost).toLocaleString()}` : '£0' },
            { label: t('service.parts_cost'), value: `£${totalPartsCost.toLocaleString()}` },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{t('service.total')}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>£{totalCost.toLocaleString()}</span>
          </div>
        </Card>

        {/* Audit trail */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.audit_trail')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconUser size={14} color="#185FA5" stroke={1.5} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{t('service.record_created_by')} {record.sealedBy || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{formatDateTime(record.createdAt)}</div>
            </div>
          </div>
          {record.sealed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconLock size={14} color="#185FA5" stroke={1.5} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{t('service.record_sealed_by')} {record.sealedBy || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{formatDateTime(record.sealedAt)}</div>
              </div>
            </div>
          )}
        </Card>

      </div>
    </Layout>
  );
}